import { z } from 'zod';

export const FLEET_RECOVERY_MAX_ITEMS = 4096;
export const FLEET_RECOVERY_MAX_RECEIPT_BYTES = 1024 * 1024;
export const FLEET_RECOVERY_ENVELOPE_DOMAIN = 'playcast-recovery-envelope:v2\n';
export const FLEET_RECOVERY_DEVICE_DOMAIN = 'playcast-recovery-v2';
export const fleetRecoveryIdSchema = z.string().min(1).max(128).regex(/^[A-Za-z0-9][A-Za-z0-9._:-]*$/);
export const fleetRecoveryDeviceIdSchema = z.string().min(1).max(256).regex(/^[\x21-\x7e]+$/);
export const fleetRecoverySha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
export const fleetRecoveryUint64Schema = z.string().regex(/^(0|[1-9][0-9]{0,19})$/)
  .refine(value => value.length < 20 || value <= '18446744073709551615', 'Exceeds uint64');
export const fleetRecoveryUtcSchema = z.string().datetime().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/)
  .refine(value => { try { return new Date(value).toISOString() === value; } catch { return false; } }, 'Requires canonical millisecond UTC');
export const fleetRecoveryVersionSchema = z.number().int().min(1).max(0x7fffffff);
export const fleetRecoveryUuidSchema = z.string().uuid();

/** Browser-safe canonical base64 validation; wire signatures must have exact byte lengths. */
export function isCanonicalBase64(value: string, byteLength?: number): boolean {
  if (!/^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/.test(value)) return false;
  const bytes = value.length * 3 / 4 - (value.endsWith('==') ? 2 : value.endsWith('=') ? 1 : 0);
  if (byteLength !== undefined && bytes !== byteLength) return false;
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  if (value.endsWith('==') && (alphabet.indexOf(value[value.length - 3]) & 15) !== 0) return false;
  if (value.endsWith('=') && !value.endsWith('==') && (alphabet.indexOf(value[value.length - 2]) & 3) !== 0) return false;
  return true;
}
export const fleetRecoverySignatureSchema = z.string().length(88).refine(value => isCanonicalBase64(value, 64));
export const fleetRecoveryNonceSchema = z.string().min(44).max(344).refine(value => {
  const padding = value.endsWith('==') ? 2 : value.endsWith('=') ? 1 : 0;
  return isCanonicalBase64(value) && value.length * 3 / 4 - padding >= 32;
});
export const fleetRecoveryPublicKeySchema = z.string().length(124)
  .refine(value => value.startsWith('MFkwEwYHKoZIzj0CAQYIKoZIzj0DAQcDQgAE') && isCanonicalBase64(value, 91), 'Requires P-256 SPKI DER');

/** Lexical validation supplements, and never replaces, opened-handle native path verification. */
export class FleetRecoveryPathPolicy {
  public static isLocalAbsolute(value: string): boolean {
    if (!/^[A-Za-z]:\\/.test(value) || /[\x00-\x1f/<>"|?*]/.test(value)) return false;
    const components = value.slice(3).split('\\');
    return components.length > 0 && components.every(part => part.length > 0 && part !== '.' && part !== '..'
      && !/[. :]$/.test(part) && !part.includes(':')
      && !/^(con|prn|aux|nul|com[1-9]|lpt[1-9])(?:\.|$)/i.test(part));
  }

  public static isRegistry(value: string): boolean {
    return /^(HKLM|HKCU|HKU)\\[^\x00-\x1f/]+$/.test(value)
      && value.split('\\').every(part => part.length > 0 && part !== '.' && part !== '..' && !/[. ]$/.test(part));
  }

  public static isRecoveryLocator(value: string): boolean {
    return /(?:^|\\)PlaycastRecovery(?:\\|$)/i.test(value)
      || /^(?:\\)?(?:PlaycastUpdateService|PlaycastRecoveryRescue(?:Candidate-[A-Za-z0-9-]+)?|PlaycastRecoveryWorker-[A-Za-z0-9-]+)$/i.test(value);
  }

  public static isWithin(value: string, root: string): boolean {
    const canonical = value.toLowerCase(), canonicalRoot = root.toLowerCase();
    return canonical === canonicalRoot || canonical.startsWith(`${canonicalRoot}\\`);
  }
}
export const fleetRecoveryPathSchema = z.string().max(32767).refine(FleetRecoveryPathPolicy.isLocalAbsolute, 'Requires canonical local absolute path');
export const fleetRecoveryRegistrySchema = z.string().max(32767).refine(FleetRecoveryPathPolicy.isRegistry, 'Requires canonical registry path');
export const fleetRecoveryHttpsSchema = z.string().max(4096).url().refine(value => {
  try { const url = new URL(value); return url.protocol === 'https:' && !url.username && !url.password && !url.hash; }
  catch { return false; }
}, 'Requires HTTPS without credentials or fragment');
